<template>
  <div class="history-container">
    <div class="page-header">
      <div class="breadcrumb">ວິຊາການ / <span>ປະຫວັດການເລືອນຊັ້ນ</span></div>
      <div class="header-content">
        <h1 class="page-title">ປະຫວັດການເລືອນຊັ້ນນັກຮຽນ</h1>
        <p>ກວດສອບລາຍການເລືອນຊັ້ນ ແລະ ຍົກເລີກລາຍການທີ່ຜິດພາດ</p>
      </div>
    </div>

    <!-- ຕາຕະລາງປະຫວັດ -->
    <div class="panel table-panel">
      <div class="panel-head">
        <div class="search-box">
          <div class="input-container">
            <i class="fas fa-search search-icon"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              @keyup.enter="handleSearch"
              placeholder="ຄົ້ນຫາດ້ວຍ ຊື່ ຫຼື ລະຫັດນັກຮຽນ..."
            >
          </div>
          <button class="btn-search" @click="handleSearch">ຄົ້ນຫາ</button>
        </div>
      </div>
      <div class="panel-body">
        <div v-if="loading" class="text-center py-5">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p class="mt-2">ກຳລັງໂຫຼດຂໍ້ມູນ...</p>
        </div>

        <div v-else-if="historyList.length === 0" class="empty-state py-5 text-center">
          <i class="fas fa-folder-open fa-3x mb-3" style="opacity: 0.2"></i>
          <p>ຍັງບໍ່ມີປະຫວັດການເລືອນຊັ້ນ</p>
        </div>

        <div v-else class="table-wrap mt-2">
          <table class="history-table">
            <thead>
              <tr>
                <th>ວັນທີດຳເນີນການ</th>
                <th>ນັກຮຽນ</th>
                <th>ຈາກ (ຊັ້ນ/ຫ້ອງ/ສົກຮຽນ)</th>
                <th>ໄປຫາ (ຊັ້ນ/ຫ້ອງ/ສົກຮຽນ)</th>
                <th>ຜູ້ດຳເນີນການ</th>
                <th class="text-center" width="120">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="h in historyList" :key="h._id">
                <td>{{ formatDate(h.promotion_date) }}</td>
                <td>
                  <div class="student-info">
                    <span class="name">{{ h.student_name }}</span>
                    <span class="id">{{ h.student_custom_id }}</span>
                  </div>
                </td>
                <td class="cell-grade">{{ h.from_grade_name || h.from_grade }} <span class="divider">|</span> ຫ້ອງ {{ h.from_room }} <span class="divider">|</span> {{ h.from_year }}</td>
                <td class="cell-grade"><strong class="text-success">{{ h.to_grade_name || h.to_grade }} <span class="divider">|</span> ຫ້ອງ {{ h.to_room }} <span class="divider">|</span> {{ h.to_year }}</strong></td>
                <td><i class="fas fa-user-circle"></i> {{ h.promoted_by }}</td>
                <td class="text-center">
                  <button class="btn-action-undo" @click="showConfirm(h)" :disabled="undoing === h._id" title="ຍົກເລີກການເລືອນຊັ້ນ">
                    <i class="fas" :class="undoing === h._id ? 'fa-spinner fa-spin' : 'fa-history'"></i> ຍົກເລີກ
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- ສ່ວນຄວບຄຸມ Pagination ທີ່ Sync style ແລ້ວ -->
         <div class="pagination-container">
        <div class="pagination-info">ສະແດງ {{ startItem }} - {{ endItem }} ຈາກທັງໝົດ {{ totalRows }} ລາຍການ</div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">«</button>
          <template v-for="(page, idx) in visiblePages">
            <span v-if="page === '...'" :key="'e' + idx" class="page-ellipsis">…</span>
            <button v-else :key="page"
                    class="page-btn"
                    :class="{ active: currentPage === page }"
                    @click="changePage(page)">{{ page }}</button>
          </template>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">»</button>
        </div>
      </div>
    </div>

    <!-- Modal ຢືນຢັນ (Confirm Modal) -->
    <transition name="modal">
      <div v-if="confirmModal.show" class="modal-overlay" @mousedown.self="confirmModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon warning">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="modal-content-area">
            <h2>ຢືນຢັນການຍົກເລີກ</h2>
            <p>ທ່ານຕ້ອງການຍົກເລີກການເລືອນຊັ້ນຂອງ <strong>{{ confirmModal.studentName }}</strong> ແລະ ຖອຍຂໍ້ມູນກັບຄືນສະຖານະເດີມແມ່ນບໍ່?</p>
          </div>
          <div class="modal-footer-actions">
            <button class="btn-modal-secondary" @click="confirmModal.show = false">ຍົກເລີກ</button>
            <button class="btn-modal-primary error" @click="handleUndo">ຕົກລົງ, ດຳເນີນການ</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal ແຈ້ງຜົນຮັບ (Result Modal) -->
    <transition name="modal">
      <div v-if="resultModal.show" class="modal-overlay" @mousedown.self="resultModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon" :class="resultModal.type">
            <i class="fas" :class="resultModal.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'"></i>
          </div>
          <div class="modal-content-area">
            <h2>{{ resultModal.title }}</h2>
            <p>{{ resultModal.message }}</p>
          </div>
          <div class="modal-footer-actions">
            <button 
              class="btn-modal-primary" 
              :class="resultModal.type" 
              @click="resultModal.show = false"
            >
              ເຂົ້າໃຈແລ້ວ
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';
import { getPaginationRange } from '@/utils/pagination';

export default {
  name: 'PromotionHistoryPage',
  data() {
    return {
      loading: false,
      undoing: null,
      historyList: [],
      currentPage: 1,
      pageSize: 20,
      totalRows: 0,
      searchQuery: '',
      searchTimer: null,
      confirmModal: { show: false, historyId: null, studentName: '' },
      resultModal: { show: false, type: 'success', title: '', message: '' }
    }
  },
  watch: {
    searchQuery() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.handleSearch();
      }, 500);
    }
  },
  mounted() {
    this.fetchHistory();
  },
  computed: {
    totalPages() { return Math.ceil(this.totalRows / this.pageSize) || 1; },
    visiblePages() { return getPaginationRange(this.currentPage, this.totalPages); },
    startItem() { return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; },
    endItem() { 
      const end = this.currentPage * this.pageSize;
      return end > this.totalRows ? this.totalRows : end;
    }
  },
  methods: {
    async fetchHistory() {
      this.loading = true;
      try {
        const res = await api.get('/students/promote/history', {
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            search: this.searchQuery
          }
        });
        if (res.data.code === 1) {
          this.historyList = res.data.rows;
          this.totalRows = res.data.totalRows || 0;
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchHistory();
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('lo-LA');
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchHistory();
      }
    },
    showConfirm(history) {
      this.confirmModal = {
        show: true,
        historyId: history._id,
        studentName: history.student_name
      };
    },
    showResult(type, title, message) {
      this.resultModal = { show: true, type, title, message };
    },
    async handleUndo() {
      const historyId = this.confirmModal.historyId;
      this.confirmModal.show = false;
      this.undoing = historyId;
      try {
        const res = await api.post('/students/promote/undo', { historyId });
        if (res.data.code === 1) {
          this.showResult('success', 'ຍົກເລີກສຳເລັດ', 'ຂໍ້ມູນນັກຮຽນຖືກປັບຄືນເປັນຄ່າເດີມແລ້ວ');
          this.fetchHistory();
        }
      } catch (e) {
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດຍົກເລີກລາຍການນີ້ໄດ້');
      } finally {
        this.undoing = null;
      }
    }
  }
}
</script>

<style scoped>
.history-container { animation: fadeIn 0.4s ease-out; max-width: 1200px; margin: 0 auto; }

.page-title { color: var(--lao); font-weight: 700; letter-spacing: -0.01em; }
.breadcrumb span { font-weight: 600; }

.table-panel { border-radius: 12px; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05); }

.search-box { display: flex; gap: 10px; padding: 15px 20px; background: #fcfcfc; border-radius: 10px 10px 0 0; width: 100%; }
.input-container { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); }
.input-container input { width: 100%; padding: 10px 10px 10px 35px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; transition: border-color 0.2s; }
.input-container input:focus { outline: none; border-color: var(--lao); }
.btn-search { background: var(--lao); color: white; border: none; padding: 0 20px; border-radius: 8px; cursor: pointer; font-weight: 500; }

.table-wrap { max-height: 65vh; overflow-y: auto; border: 1px solid var(--border); border-radius: 8px; }
.history-table { width: 100%; border-collapse: separate; border-spacing: 0; background: white; }

.history-table td { padding: 15px; border-bottom: 1px solid #edf2f7; font-size: 14px; vertical-align: middle; transition: background 0.2s; }
.history-table tr:hover td { background-color: #f8fafc; }
.history-table tr:nth-child(even) { background-color: #fafafa; }

.cell-grade { color: var(--text2); font-size: 13px; }
.divider { color: #cbd5e1; margin: 0 4px; font-weight: 300; }

.student-info { display: flex; flex-direction: column; }
.student-info .name { font-weight: 600; color: var(--text1); }
.student-info .id { font-size: 12px; color: #94a3b8; font-family: monospace; }

.text-success { color: #10b981; font-weight: 700; }

.btn-action-undo {
  background: #fff1f2; color: #be123c; border: 1px solid #fecdd3;
  padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 12px;
  font-weight: 700; transition: all 0.2s; display: inline-flex; align-items: center; gap: 6px;
}
.btn-action-undo:hover:not(:disabled) { 
  background: #be123c; color: white; border-color: #be123c; 
  transform: translateY(-1px); box-shadow: 0 4px 6px -1px rgba(190, 18, 60, 0.2); 
}
.btn-action-undo:active { transform: translateY(0); }
.btn-action-undo:disabled { opacity: 0.5; cursor: not-allowed; }

/* Scrollbar Styling */
.table-wrap::-webkit-scrollbar { width: 8px; height: 8px; }
.table-wrap::-webkit-scrollbar-track { background: #f1f5f9; }
.table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

.pagination-container { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-top: 1px solid var(--border); }
.page-btn { padding: 5px 12px; border: 1px solid var(--border); background: white; border-radius: 4px; cursor: pointer; margin: 0 2px; }
.page-btn.active { background: var(--lao); color: white; border-color: var(--lao); }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-ellipsis { padding: 5px 6px; color: var(--text3); }
/* Modal Overlay Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

/* Result & Confirm Modal Custom Styling */
.result-modal { padding: 40px 30px; max-width: 400px !important; text-align: center; background: white; border-radius: 16px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
.modal-icon { font-size: 64px; margin-bottom: 20px; }
.modal-icon.success { color: #10b981; }
.modal-icon.error { color: #ef4444; }
.modal-icon.warning { color: #f59e0b; }

.modal-content-area h2 { margin: 0 0 10px; font-size: 1.6rem; color: #0f172a; font-weight: 800; }
.modal-content-area p { color: #334155; font-size: 1.05rem; line-height: 1.6; margin-bottom: 30px; font-weight: 500; }

.modal-footer-actions { display: flex; gap: 12px; justify-content: center; }

.btn-modal-primary {
  padding: 12px 24px; border: none; border-radius: 10px; cursor: pointer;
  font-weight: 600; font-size: 14px; background: var(--lao); color: white;
  transition: 0.2s; min-width: 140px;
}
.btn-modal-primary:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 63, 138, 0.2); }
.btn-modal-primary.success { background: #10b981; }
.btn-modal-primary.success:hover { background: #059669; }
.btn-modal-primary.error { background: #ef4444; }
.btn-modal-primary.error:hover { background: #dc2626; }

.btn-modal-secondary {
  padding: 12px 24px; border: 1.5px solid #e2e8f0; border-radius: 10px;
  cursor: pointer; font-weight: 600; font-size: 14px; background: white;
  color: #475569; transition: 0.2s; min-width: 120px;
}
.btn-modal-secondary:hover { background: #f8fafc; color: var(--text1); }

/* Modal Animation */
.modal-enter-active, .modal-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter-active .custom-modal-card { animation: modalIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.modal-leave-active .custom-modal-card { animation: modalOut 0.2s ease-in; }

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.8) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes modalOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.9); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>